/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewProjectConfig.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import { CustomExportButton } from "../../../components";
import ViewProject from "../../../components/customComponentsV2/projectConfig/ViewProject";
import ViewVariant from "../../../components/customComponentsV2/projectConfig/ViewVariant";
import ViewPCDetails from "../../../components/customComponentsV2/projectConfig/ViewPCDetails";
import ShowLogsDetails from "../../../components/customComponentsV2/projectConfig/ShowLogsDetails";
import MoreProjectDetails from "../../../components/customComponentsV2/projectConfig/MoreProjectDetails";
import MoreVariantDetails from "../../../components/customComponentsV2/projectConfig/MoreVariantDetails";
import ShowVariantDetails from "../../../components/customComponentsV2/projectConfig/ShowVariantDetails";
import ShowVarientRequestDetails from "../../../components/customComponentsV2/projectConfig/ShowVarientRequestDetails";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { HandleApiActions } from "../../../redux/actions";
import { adminSettingsAPI, projectConfigAPI } from "../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import AddDialog from "../../../components/customComponentsV2/projectConfig/AddDialog";
import CustomFormTextField from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomMultiSelectV2 from "../../../components/customComponentsV2/common/multiselect/CustomMultiSelectV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomDialogBoxV2 from "../../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";

import CustomDatePicker from "../../../components/customComponentsV2/common/datepicker/CustomDatePicker";
import CustomSelectVariantColor from "../../../components/customComponentsV2/common/colorpicker/CustomSelectVariantColor";
import editRequestAPI from "../../../redux/actions/editRequestApi";
import moment from "moment";
import CustomTabsV2 from "../../../components/customComponentsV2/common/tabs/CustomTabsV2";
import CustomSelectV3 from "../../../components/customComponentsV2/common/select/CustomSelectV3";
import CustomDatePickerVariant from "../../../components/customComponentsV2/common/datepicker/CustomDatePickerVariant";
import CustomFormTextFieldV3 from "../../../components/customComponentsV2/common/textField/CustomFormTextFieldV3";
import { showToastMessage } from "../../../redux/actions/actionHandler";
import HomologationPlanning from "../../../components/customComponentsV2/projectConfig/HomologationPlanning";

const createMilestoneRow = (overrides = {}) => ({
  id:
    (typeof crypto !== "undefined" &&
      crypto.randomUUID &&
      crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  name: "",
  milestoneDate: null,
  ...overrides,
});

const parseMilestoneDate = (milestoneDate) => {
  if (!milestoneDate) return null;
  const parsedDate = moment(milestoneDate, ["MM-YYYY", "YYYY-MM"], true);
  if (parsedDate.isValid()) return parsedDate.startOf("month");

  const fallbackDate = moment(milestoneDate);
  return fallbackDate.isValid() ? fallbackDate.startOf("month") : null;
};

const mapProjectMilestonesToRows = (milestones = []) => {
  if (!Array.isArray(milestones) || milestones.length === 0) {
    return [];
  }

  return milestones.map((milestone) =>
    createMilestoneRow({
      name: milestone?.name || "",
      milestoneDate: parseMilestoneDate(milestone?.milestone_date),
      sequence: milestone?.sequence,
    }),
  );
};

const milestoneDatePickerSx = {
  width: "160px !important",
  height: "250px !important",
  "& .MuiPickersLayout-root": {
    minWidth: "0 !important",
    maxWidth: "160px !important",
  },
  "& .MuiPickersLayout-contentWrapper": {
    padding: 0,
  },
  "& .MuiPickersCalendarHeader-switchViewButton": {
    display: "none",
  },
  "& .MuiPickersCalendarHeader-root": {
    position: "relative",
    paddingLeft: "1px",
    paddingRight: "1px",
    justifyContent: "space-between",
    minHeight: "30px",
    marginBottom: 0,
  },
  "& .MuiPickersCalendarHeader-labelContainer": {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
  },
  "& .MuiPickersCalendarHeader-label": {
    fontSize: "14px",
  },
  "& .MuiPickersArrowSwitcher-root": {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
  },
  "& .MuiPickersArrowSwitcher-spacer": {
    display: "none",
  },
  "& .MuiPickersArrowSwitcher-button": {
    padding: 0,
  },
  "& .MuiPickersYear-yearButton.Mui-selected": {
    backgroundColor: "#000000 !important",
    color: "#FFFFFF !important",
    "&:hover": {
      backgroundColor: "#000000 !important",
    },
    borderRadius: "0px !important",
  },
  "& .MuiPickersMonth-monthButton.Mui-selected": {
    backgroundColor: "#000000 !important",
    color: "#FFFFFF !important",
    "&:hover": {
      backgroundColor: "#000000 !important",
    },
    borderRadius: "0px !important",
  },
};


const ProjectConfigHeader = ({
  onBack,
  exportProjectDetails,
  exportProjectVariantsDetails,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 2,
    }}
  >
    <CustomButtonV2
      startIcon={<ArrowBackIcon />}
      type="submit"
      btnName="Back"
      size="small"
      handleOnClick={onBack}
    />

    <Box sx={{ display: "flex", gap: 1 }}>
      <CustomExportButton
        fileName={"Project_Configurator"}
        exportDataJson={{
          ...exportProjectDetails?.[0],
          variants: exportProjectVariantsDetails || [],
        }}
        exportDataExcel={{
          project_details:
            exportProjectDetails?.map((pr) => ({
              ...pr,
              manufacture_details: JSON.stringify(pr?.manufacture_details) || "",
              ...(pr?.zones ? { zones: JSON.stringify(pr.zones) } : {}),
              vehicle_class_details:
                JSON.stringify(pr?.vehicle_class_details) || "",
              powertrain_class_details:
                JSON.stringify(pr?.powertrain_class_details) || "",
              vehicle_function_details:
                JSON.stringify(pr?.vehicle_function_details) || "",
              delivery_market: pr?.delivery_market?.join(", ") || "",
              co_owner_details: pr?.co_owner_details?.length
                ? JSON.stringify(pr?.co_owner_details)
                : "",
              edit_user_details: pr?.edit_user_details?.length
                ? JSON.stringify(pr?.edit_user_details)
                : "",
              view_user_details: pr?.view_user_details?.length
                ? JSON.stringify(pr?.view_user_details)
                : "",
              model_year: JSON.stringify(pr?.model_year) || "",
            })) || [],
          variants_details:
            exportProjectVariantsDetails?.map((vr) => ({
              ...vr,
              powertrain_class_details:
                JSON.stringify(vr?.powertrain_class_details) || "",
              vehicle_function_details:
                JSON.stringify(vr?.vehicle_function_details) || "",
              manufacture_details:
                JSON.stringify(vr?.manufacture_details) || "",
              co_owner_details: vr?.co_owner_details?.length
                ? JSON.stringify(vr?.co_owner_details)
                : "",
              edit_user_details: vr?.edit_user_details?.length
                ? JSON.stringify(vr?.edit_user_details)
                : "",
              view_user_details: vr?.view_user_details?.length
                ? JSON.stringify(vr?.view_user_details)
                : "",
              model_year: JSON.stringify(vr?.production_year) || "",
            })) || [],
        }}
      />
    </Box>
  </Box>
);

const ProjectConfigMainLayout = ({
  tabsDataProject,
  expandPC,
  handleExpandPC,
  handleEditClick,
  logsVariant,
  expandedIndex,
  setExpandedIndex,
  variantDetailsSelected,
  expandVC,
  handleExpandVC,
  handleVariantToggle,
  handleAddVariant,
  loading,
  handleExpandVCLogs,
  expandVCLogs,
  vehicleStatusData,
  logsCountVC,
  rowsPerPageVCLogCount,
  pageVCLogCount,
  handlePageChangeLogsVC,
  isReceratedProject,
  handleRecreateVariantClick,
  handleBackToPC,
  tabsData1,
  tableProjectConfigs,
  projectId,
}) => (
  <Grid container>
    <Grid item xs={12} md={5}>
      <Box
        sx={{
          borderRadius: 0,
          minHeight: "600px",
          maxHeight: "600px",
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#a0a0a0",
          },
        }}
      >
        <ViewProject
          tabsData={tabsDataProject}
          expanded={expandPC}
          setExpanded={handleExpandPC}
        />
        <ViewVariant
          handleEditClick={handleEditClick}
          logs={logsVariant}
          expandedIndex={expandedIndex}
          setExpandedIndex={setExpandedIndex}
          variantDetailsSelected={variantDetailsSelected}
          expanded={expandVC}
          setExpanded={handleExpandVC}
          handleVariantToggle={handleVariantToggle}
          handleAddVariant={handleAddVariant}
          loading={loading}
          setExpandVCLogs={handleExpandVCLogs}
          expandVCLogs={expandVCLogs}
          vehicleStatusData={vehicleStatusData}
          logsCount={logsCountVC}
          rowsPerPageVCLogCount={rowsPerPageVCLogCount}
          pageVCLogCount={pageVCLogCount}
          handlePageChangeLogsVC={handlePageChangeLogsVC}
          isRecreatedProject={isReceratedProject}
          onRecreateVariantClick={handleRecreateVariantClick}
        />
      </Box>
    </Grid>
    <Grid item xs={12} md={7}>
      <Box
        sx={{
          borderRadius: 0,
          minHeight: "600px",
          maxHeight: "600px",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "8px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#a0a0a0",
          },
        }}
      >
        <Box>
          {expandVC ? (
            <>
              <CustomButtonV2
                startIcon={<ArrowBackIcon />}
                type="submit"
                size="small"
                handleOnClick={handleBackToPC}
              />
              <Box sx={{ mt: 2 }}>
                <MoreVariantDetails tabsData={tabsData1} />
              </Box>
            </>
          ) : (
            <CustomTabsV2
              tabs={[
                {
                  name: "Details",
                  count: tableProjectConfigs?.length || "",
                  content: (
                    <MoreProjectDetails tableProjectConfigs={tableProjectConfigs} />
                  ),
                },
                {
                  name: "Homologation Planning",
                  count: "",
                  content: (
                    <HomologationPlanning projectId={projectId} />
                  ),
                },
              ]}
            />
          )}
        </Box>
      </Box>
    </Grid>
  </Grid>
);

const buildLabelValueOptions = (items, getLabel, getValue) =>
  (items || []).map((item) => ({
    label: getLabel(item),
    value: getValue(item),
  }));

const getSelectedValueIds = (selectedValuesUpdateProject, title) =>
  selectedValuesUpdateProject?.[title]?.map((data) =>
    parseInt(data?.value),
  ) || [];

const filterOutSelectedIds = (options, selectedIds) =>
  options.filter((option) => !selectedIds.includes(option.value));

const isOptionSelected = (option, selected) => {
  if (typeof option.value === "object") {
    return (
      JSON.stringify(option?.value) === JSON.stringify(selected?.value)
    );
  }
  return option?.value === selected?.value;
};

const getNonEmptyUsers = (users = []) =>
  (users || []).filter((u) => Object.keys(u || {}).length > 0);

const getUserDisplayName = (user = {}) =>
  user?.fullname || user?.name || user?.email || "-";

const getUniqueUsersByNameEmail = (users = []) =>
  [
    ...new Map(
      (users || []).map((user) => [
        `${getUserDisplayName(user)}-${user?.email || "NO_EMAIL"}`,
        user,
      ]),
    ).values(),
  ];

const renderUserTable = (users, widthFallback) => (
  <Table
    stickyHeader
    size="small"
    sx={{
      tableLayout: "fixed",
      width: "100%",
    }}
  >
    <TableBody>
      {users.length > 0 ? (
        users.map((user) => {
          const displayName = getUserDisplayName(user);
          const displayEmail = user?.email || "-";
          return (
            <TableRow key={user?.user_id}>
              <TableCell sx={{ width: user?.width || widthFallback }}>
                {`${displayName} - ${displayEmail}`}
              </TableCell>
            </TableRow>
          );
        })
      ) : (
        <TableRow>
          <TableCell align="center" sx={{ py: 2 }}>
            No data available
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
);

const buildTabsDataUsersPC = (projectDetailsSelected) => {
  const ownerUsers = Array.from(
    getUniqueUsersByNameEmail(projectDetailsSelected?.owner_details || []),
  );
  const editorUsers = [
    ...getNonEmptyUsers(projectDetailsSelected?.co_owner_details),
    ...getNonEmptyUsers(projectDetailsSelected?.edit_user_details),
  ];
  const viewerUsers = getNonEmptyUsers(projectDetailsSelected?.view_user_details);

  return [
    {
      name: "Owner",
      count: ownerUsers.length || 0,
      content: renderUserTable(ownerUsers, "150px"),
    },
    {
      name: "Editor",
      count: editorUsers.length || 0,
      content: renderUserTable(editorUsers, "200px"),
    },
    {
      name: "Viewer",
      count: viewerUsers.length || 0,
      content: renderUserTable(viewerUsers, "150px"),
    },
  ];
};

const buildTabsDataUsersVC = (selectedVariantDetails) => {
  const ownerUsers = Array.from(
    getUniqueUsersByNameEmail(
      getNonEmptyUsers(selectedVariantDetails?.owner_details),
    ),
  );
  const editorUsers = [
    ...getNonEmptyUsers(selectedVariantDetails?.co_owner_details),
    ...getNonEmptyUsers(selectedVariantDetails?.edit_user_details),
  ];
  const viewerUsers = getNonEmptyUsers(selectedVariantDetails?.view_user_details);

  return [
    {
      name: "Owner",
      count: ownerUsers.length || 0,
      content: renderUserTable(ownerUsers, "150px"),
    },
    {
      name: "Editor",
      count: editorUsers.length || 0,
      content: renderUserTable(editorUsers, "200px"),
    },
    {
      name: "Viewer",
      count: viewerUsers.length || 0,
      content: renderUserTable(viewerUsers, "150px"),
    },
  ];
};

function ViewProjectConfig() {
  dayjs.extend(customParseFormat);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { variantInfo } = useSelector((state) => state.variantReducer);
  const {
    projectId: stateProjectId,
    powerTrainClassData,
    adminDefaultData,
    vehicleFunctionData,
    metaData,
  } = location.state || {};
  const queryParams = new URLSearchParams(location.search);
  const projectId = stateProjectId || queryParams.get("project_id");

  //useStates
  const [expandPC, setExpandPC] = useState(true);
  const [expandVC, setExpandVC] = useState(false);
  const [expandPCLogs, setExpandPCLogs] = useState(false);
  const [expandVCLogs, setExpandVCLogs] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [logsProject, setLogsProject] = useState([]);
  const [logsCount, setLogsCount] = useState(0);
  const [logsCountVC, setLogsCountVC] = useState(0);
  const [logsVariant, setLogsVariant] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVariantDetails, setSelectedVariantDetails] = useState(null);
  const [openAddVariant, setOpenAddVariant] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [dialogTitleProject, setDialogTitleProject] = useState("");
  const [editSaveIndex, setEditSaveIndex] = useState(null);

  const [projectDetailsSelected, setProjectDetailsSelected] = useState(null);
  const [isReceratedProject, setIsRecreatedProject] = useState(false);
  const [variantDetailsSelected, setVariantDetailsSelected] = useState([]);
  const [variantName, setVariantName] = useState("");
  const [modelYear, setModelYear] = useState(null);
  const [variantColor, setVariantColor] = useState("#000000");
  const [deliveryMarkets, setDeliveryMarkets] = useState(null);
  const [vehicleFunctions, setVehicleFunctions] = useState([]);
  const [powertrainClasses, setPowertrainClasses] = useState(null);
  const [manufacturerDetails, setManufacturerDetails] = useState(null);
  const [milestoneRows, setMilestoneRows] = useState([]);
  const [milestoneDraft, setMilestoneDraft] = useState(createMilestoneRow());
  const [openUpdateMilestoneDialog, setOpenUpdateMilestoneDialog] =
    useState(false);
  const [updateMilestoneTarget, setUpdateMilestoneTarget] =
    useState("project");
  const [updateMilestoneRows, setUpdateMilestoneRows] = useState([]);
  const [updateMilestoneDraft, setUpdateMilestoneDraft] = useState(
    createMilestoneRow(),
  );
  const [vehicleStatusData, setVehicleStatusData] = useState([]);
  const vehicleStatusList = Array.isArray(vehicleStatusData)
    ? vehicleStatusData
    : vehicleStatusData?.data || [];

  const [pageER, setPageER] = useState(1);
  const [rowsPerPageER, setRowsPerPageER] = useState(25);
  const [tableRowsDataER, setTableRowsDataER] = useState([]);
  const [editRequestCount, setEditRequestCount] = useState("");
  const [exportProjectDetails, setExportProjectDetails] = useState(null);
  const [exportProjectVariantsDetails, setExportProjectVariantsDetails] =
    useState(null);
  const handleRowPerPageER = () => {
    setRowsPerPageER((prev) => prev + 25);
    setPageER(1);
  };

  const [pagePCLogCount, setPagePCLogCount] = useState(1);
  const [rowsPerPagePCLogCount, setRowsPerPagePCLogCount] = useState(25);

  const [pageVCLogCount, setPageVCLogCount] = useState(1);
  const [rowsPerPageVCLogCount, setRowsPerPageVCLogCount] = useState(25);

  // Recreation states
  const [openRecreationVariant, setOpenRecreationVariant] = useState(false);
  const [recreationVariantsList, setRecreationVariantList] = useState([]);
  const [selectedVariantForRecreation, setSelectedVariantForRecreation] =
    useState(null);
  const [owner, setOwner] = useState([]);
  const [coOwners, setCoOwners] = useState([]);
  const [editors, setEditors] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [usersListData, setUsersListData] = useState([]);
  const [fullUsersList, setFullUsersList] = useState([]);

  const formatDialogTitle = (text) => {
    if (!text) return "";

    return (
      text
        // add space before capital letters (camelCase → camel Case)
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .toLowerCase()
        // 🔽 NEW: capitalize first letter inside brackets
        .replace(/\((\w)/g, (_, c) => `(${c.toUpperCase()}`)
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };
  const handleProjectDetails = async (params = {}) => {
    try {
      const {
        project_id,
        name,
        trade_mark,
        status,
        model_year,
        is_zonal,
        page_number,
        page_size,
        order_by_sort,
        order_by_column,
      } = params;

      // Dynamically include only defined params
      const queryParams = {
        page_number,
        ...(page_size && { page_size }),
        ...(page_number && { page_number }),

        ...(project_id && { project_id }),
        ...(name && { name }),
        ...(trade_mark && { trade_mark }),
        ...(status && { status }),
        ...(model_year && { model_year }),
        ...(is_zonal && { is_zonal }),
        ...(order_by_sort && { order_by_sort }),
        ...(order_by_column && { order_by_column }),
      };

      const resp = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_project,
          params: queryParams,
        }),
      );

      return resp;
    } catch (error) {
      console.error("Error fetching project details:", error);
    }
  };

  const handleVariantDetails = async (params = {}) => {
    try {
      const {
        project_id,
        variant_id,
        name,
        delivery_market,
        is_complete_details_required,
        sort_column,
        sort_order,
        is_for_access,
      } = params;

      // 🧩 Build query params
      const queryParams = {
        project_id,
        is_complete_details_required,

        ...(variant_id && { variant_id }),
        ...(name && { name }),

        ...(delivery_market && { delivery_market }),
        ...(sort_column && { sort_column }),
        ...(sort_order && { sort_order }),
        ...(is_for_access && { is_for_access }),
      };

      // Example dispatch (you can customize)
      const resp = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_variant_details,
          params: queryParams,
        }),
      );

      return resp;
    } catch (error) {
      console.error("❌ Error fetching variant details:", error.message);
      throw error;
    }
  };
  const fetchProjectAndVariantDetails = async () => {
    if (!projectId) return;

    try {
      const [respProjectDetails, respVariantDetails] = await Promise.all([
        handleProjectDetails({
          project_id: projectId,
          order_by_sort: "ASC",
        }),
        handleVariantDetails({
          project_id: projectId,
          is_complete_details_required: true,
          page_number: 1,
          page_size: 10000,
          order_by_sort: "ASC",
        }),
      ]);

      const projectData = respProjectDetails?.data?.[0];
      const variantData = respVariantDetails?.data;

      setProjectDetailsSelected(projectData || null);
      setVariantDetailsSelected(variantData || []);

      console.log("----selectedProjectDetails--", projectData);
      console.log("---selectedVariantDetails---", variantData);
      return {
        projectData,
        variantData,
      };
    } catch (error) {
      console.error("❌ Error fetching project/variant details:", error);
    }
  };

  useEffect(() => {
    fetchProjectAndVariantDetails();
  }, [projectId]);
  console.log("projectDetailsSelected", projectDetailsSelected);
  const [selectedValuesUpdateProject, setSelectedValuesUpdateProject] =
    useState({
      "VEHICLE FUNCTION(PROJECT)": [],
      "VEHICLE FUNCTION(VARIANT)": [],
      "POWERTRAIN CLASS": [],
      "DELIVERY MARKET": "",
      "MANUFACTURER CONFIGURATIONS": "",
    });

  useEffect(() => {
    if (projectDetailsSelected || selectedVariantDetails) {
      // added to trace recreated project
      setIsRecreatedProject(
        projectDetailsSelected?.parent_project_id ? true : false,
      );

      setSelectedValuesUpdateProject({
        "VEHICLE FUNCTION(PROJECT)":
          projectDetailsSelected?.vehicle_function_details?.map((item) => ({
            label: `${item.name} (${item.abbreviation})`,
            value: item?.id,
          })) || [],
        "VEHICLE FUNCTION(VARIANT)":
          selectedVariantDetails?.vehicle_function_details?.map((item) => ({
            label: `${item.name} (${item.abbreviation})`,
            value: item?.id,
          })) || [],
        "POWERTRAIN CLASS":
          projectDetailsSelected?.powertrain_class_details?.map((item) => ({
            label: `${item.name} (${item.abbreviation})`,
            value: item?.id,
          })) || [],
        // "DELIVERY MARKET":
        //   projectDetailsSelected?.delivery_market?.map((item) => ({
        //     label: item,
        //     value: item,
        //   })) || [],
        // "MANUFACTURER CONFIGURATIONS":
        //   projectDetailsSelected?.mft_details?.map((item) => ({
        //     label: item?.name,
        //     subLabel: item?.address,
        //     value: item,
        //   })) || [],
      });
    }
  }, [projectDetailsSelected, selectedVariantDetails]);

  console.log("--selectedValuesUpdateProject---", selectedValuesUpdateProject);
  const getOptionsList = (selectedValues = []) => {
    const selectedIds = getSelectedValueIds(
      selectedValuesUpdateProject,
      dialogTitleProject,
    );

    const optionBuilders = {
      "VEHICLE FUNCTION(PROJECT)": () =>
        buildLabelValueOptions(
          vehicleFunctionData,
          (item) => `${item.name} (${item.abbreviation})`,
          (item) => parseInt(item.id),
        ),
      "VEHICLE FUNCTION(VARIANT)": () =>
        buildLabelValueOptions(
          projectDetailsSelected?.vehicle_function_details,
          (item) => `${item.name} (${item.abbreviation})`,
          (item) => parseInt(item.id),
        ),
      "POWERTRAIN CLASS": () =>
        buildLabelValueOptions(
          powerTrainClassData,
          (item) => `${item.name} (${item.abbreviation})`,
          (item) => item.id,
        ),
    };

    const buildOptions = optionBuilders[dialogTitleProject];
    const list = buildOptions ? buildOptions() : [];
    const filteredList = filterOutSelectedIds(list, selectedIds);

    return filteredList?.filter(
      (option) =>
        !selectedValues?.some((selected) => isOptionSelected(option, selected)),
    );
  };

  const handleSelectionChange = (newSelected) => {
    setSelectedValuesUpdateProject((prev) => ({
      ...prev,
      [dialogTitleProject?.toUpperCase()]: newSelected,
    }));
  };

  const radioOptions = [
    { label: "Created", value: "created" },
    { label: "Ongoing", value: "ongoing" },
    { label: "Completed", value: "completed" },
  ];

  const milestoneNameOptions = [
    ...new Map(
      (projectDetailsSelected?.milestones || [])
        .filter((milestone) => milestone?.name)
        .map((milestone, index) => [
          milestone.name,
          {
            label: milestone.name,
            value: milestone.name,
            sequence:
              milestone.sequence ??
              vehicleStatusList.find((item) => item?.name === milestone.name)
                ?.sequence ??
              index,
          },
        ]),
    ).values(),
  ].sort(
    (a, b) =>
      (a.sequence ?? Number.MAX_SAFE_INTEGER) -
        (b.sequence ?? Number.MAX_SAFE_INTEGER) ||
      a.label.localeCompare(b.label),
  );

  const MilestoneSection = ({
    rows,
    draft,
    onAddMilestone,
    onRemoveMilestone,
  }) => {
    const [datePickerAnchor, setDatePickerAnchor] = useState(null);
    const [datePickerView, setDatePickerView] = useState("year");
    const [pendingMilestoneDate, setPendingMilestoneDate] = useState(null);

    const [activeMilestoneName, setActiveMilestoneName] = useState("");

    const milestoneDates = draft?.milestoneDates || {};

    const handleOpenDatePicker = (event, milestoneName) => {
      setDatePickerAnchor(event.currentTarget);
      setActiveMilestoneName(milestoneName);
      setPendingMilestoneDate(milestoneDates[milestoneName] || null);
      setDatePickerView("year");
    };

    const handleCloseDatePicker = () => {
      setDatePickerAnchor(null);
      setActiveMilestoneName("");
      setPendingMilestoneDate(null);
      setDatePickerView("year");
    };

    const updateMilestoneDate = (date) => {
      if (!activeMilestoneName) return;

      const selectedMilestoneOption = milestoneNameOptions.find(
        (option) => option.value === activeMilestoneName,
      );

      onAddMilestone([
        createMilestoneRow({
          name: activeMilestoneName,
          milestoneDate: date,
          sequence: selectedMilestoneOption?.sequence,
        }),
      ]);
    };

    const handleSelectedPickerClick = (event) => {
      const selectedButton = event.target.closest("button.Mui-selected");
      if (!selectedButton) return;

      const selectedText = selectedButton.textContent?.trim();
      const baseDate =
        pendingMilestoneDate || milestoneDates[activeMilestoneName] || moment();

      if (datePickerView === "year" && /^\d{4}$/.test(selectedText)) {
        setPendingMilestoneDate(
          baseDate.clone().year(Number(selectedText)).startOf("month"),
        );
        setDatePickerView("month");
        return;
      }

      if (datePickerView === "month") {
        const monthIndex = [...moment.monthsShort(), ...moment.months()]
          .findIndex(
            (month) => month.toLowerCase() === selectedText?.toLowerCase(),
          ) % 12;

        if (monthIndex === -1) return;

        updateMilestoneDate(baseDate.clone().month(monthIndex).startOf("month"));
        handleCloseDatePicker();
      }
    };

    const addedMilestoneNames = new Set(
      rows.map((milestone) => milestone.name?.trim()).filter(Boolean),
    );
    const availableMilestoneNameOptions = milestoneNameOptions.filter(
      (option) => !addedMilestoneNames.has(option.value),
    );
    const milestoneDropdownOptions =
      availableMilestoneNameOptions.length > 0
        ? availableMilestoneNameOptions
        : [{ label: "No data", value: "__no_data__" }];
    const getMilestoneSequence = (name) =>
      milestoneNameOptions.find((option) => option.value === name)?.sequence ??
      Number.MAX_SAFE_INTEGER;
    const sortedRows = rows
      .slice()
      .sort(
        (a, b) =>
          getMilestoneSequence(a.name) - getMilestoneSequence(b.name) ||
          a.name.localeCompare(b.name),
      );

    return (
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>Milestones</Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            alignItems: "end",
          }}
        >
          {milestoneDropdownOptions.map((option) => (
            <React.Fragment key={option.value}>
              <CustomFormTextFieldV3
                label="Milestone Name"
                required
                value={option.value === "__no_data__" ? "" : option.value}
                placeholder="No data"
                disabled
              />
              <Box>
                <Typography
                  sx={{
                    mb: 0.5,
                    color: "#6f7174",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                  }}
                >
                  Milestone Date <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="MM-YYYY"
                  value={milestoneDates[option.value]?.format("MM-YYYY") || ""}
                  onClick={(event) => {
                    if (option.value === "__no_data__") return;
                    handleOpenDatePicker(event, option.value);
                  }}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <CalendarMonthIcon
                        sx={{
                          fontSize: 18,
                          color: "#000",
                          cursor: "pointer",
                        }}
                      />
                    ),
                  }}
                  sx={{
                    cursor:
                      option.value === "__no_data__" ? "not-allowed" : "pointer",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                      "& fieldset": {
                        borderColor: "#d9d9d9",
                        borderRadius: "0px",
                      },
                      "&:hover fieldset": { borderColor: "#8c8c8c" },
                      "&.Mui-focused fieldset": { borderColor: "#000" },
                    },
                  }}
                />
              </Box>
            </React.Fragment>
          ))}
        </Box>

        {rows.length > 0 ? (
          <Stack
            direction="row"
            mt={2}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              maxHeight: 72,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 0.5,
              pb: 0.5,
              "&::-webkit-scrollbar": { width: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#bfbfbf",
                borderRadius: 10,
              },
            }}
          >
            {sortedRows.map((milestone) => (
              <Chip
                key={milestone.id}
                label={`${milestone.name} - ${milestone.milestoneDate?.format("MM-YYYY")}`}
                onDelete={() => onRemoveMilestone(milestone.id)}
                sx={{
                  backgroundColor: "#000",
                  color: "white",
                  borderRadius: 0,
                  px: 1.2,
                  py: 0.5,
                  mr: 1.2,
                  "& .MuiChip-deleteIcon": {
                    color: "white",
                    fontSize: 18,
                    "&:hover": {
                      color: "white",
                    },
                  },
                }}
              />
            ))}
          </Stack>
        ) : null}

        <Popover
          open={Boolean(datePickerAnchor)}
          anchorEl={datePickerAnchor}
          onClose={handleCloseDatePicker}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{ sx: { overflow: "visible", borderRadius: "0px" } }}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box onClickCapture={handleSelectedPickerClick}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                value={
                  pendingMilestoneDate ||
                  milestoneDates[activeMilestoneName] ||
                  null
                }
                onChange={(newValue) => {
                  if (!newValue) return;
                  if (datePickerView === "year") {
                    setPendingMilestoneDate(newValue.startOf("month"));
                    setDatePickerView("month");
                    return;
                  }
                  updateMilestoneDate(newValue.startOf("month"));
                  handleCloseDatePicker();
                }}
                views={["year", "month"]}
                view={datePickerView}
                slotProps={{ actionBar: { actions: [] } }}
                sx={milestoneDatePickerSx}
              />
            </Box>
          </LocalizationProvider>
        </Popover>
      </Box>
    );
  };

  const componentMap = {
    CustomFormTextField,
    CustomSelectV2,
    CustomSelectV3,
    CustomMultiSelectV2,
    CustomRadioButtonV2,
    CustomDatePicker,
    CustomSelectVariantColor,
    CustomDatePickerVariant,
    CustomFormTextFieldV3,
    MilestoneSection,
  };

  const getMilestoneSectionProps = ({
    rows,
    draft,
    setRows,
    setDraft,
  }) => ({
    rows,
    draft,
    onAddMilestone: (milestonesToAdd = []) => {
      if (Array.isArray(milestonesToAdd) && milestonesToAdd.length > 0) {
        if (
          milestonesToAdd.some(
            (milestone) => !milestone.name || !milestone.milestoneDate,
          )
        ) {
          return;
        }

        setRows((prev) => {
          const existingNames = new Set(prev.map((row) => row.name));

          return [
            ...prev,
            ...milestonesToAdd.filter(
              (milestone) => !existingNames.has(milestone.name),
            ),
          ];
        });
        setDraft(createMilestoneRow());
        return;
      }

      if (
        draft.name?.trim().length < 3 ||
        !draft.milestoneDate ||
        rows.some((milestone) => milestone.name === draft.name?.trim())
      ) {
        return;
      }

      const selectedMilestoneOption = milestoneNameOptions.find(
        (option) => option.value === draft.name?.trim(),
      );

      setRows((prev) => [
        ...prev,
        createMilestoneRow({
          name: draft.name.trim(),
          milestoneDate: draft.milestoneDate,
          sequence: selectedMilestoneOption?.sequence,
        }),
      ]);
      setDraft(createMilestoneRow());
    },
    onRemoveMilestone: (id) =>
      setRows((prev) => prev.filter((row) => row.id !== id)),
  });

  const formLayout = [
    ...(openRecreationVariant
      ? [
        {
          component: "CustomSelectV3",
          props: {
            placeholder: "Select Parent Variant",
            label: "Parent Variant",
            // infoname: "manufacturer_name",
            required: true,
            selectedValue: selectedVariantForRecreation,
            handleOnChange: (e) => {
              setSelectedVariantForRecreation(e.target.value);
              setFormStates(e.target.value);
            },

            optionsList:
              recreationVariantsList.map((item) => ({
                label: item?.name,
                value: item,
              })) || [],
          },
        },
      ]
      : []),
    {
      component: "CustomFormTextFieldV3",
      props: {
        label: "Variant Name",
        required: true,
        type: "text",
        fldType: "text",
        value: variantName,
        setValue: setVariantName,
        placeholder: "Enter Variant Name",
        infoname: "variant_name",
      },
    },
    ...(openAddVariant || openRecreationVariant
      ? [
        {
          component: "MilestoneSection",
          props: getMilestoneSectionProps({
            rows: milestoneRows,
            draft: milestoneDraft,
            setRows: setMilestoneRows,
            setDraft: setMilestoneDraft,
          }),
        },
      ]
      : []),
    {
      component: "CustomDatePickerVariant",
      props: {
        label: "Select a Model Year",
        required: true,
        value: modelYear,
        onChange: setModelYear,
      },
    },
    {
      component: "CustomSelectVariantColor",
      props: {
        label: "Variant Color",
        infoname: "variant_color",
        required: true,
        value: variantColor,
        setValue: setVariantColor,
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select Delivery Market",
        label: "Delivery Market",
        infoname: "delivery_market",
        required: true,
        selectedValue: deliveryMarkets,
        handleOnChange: (e) => setDeliveryMarkets(e.target.value),

        optionsList:
          projectDetailsSelected?.delivery_market?.map((item) => ({
            label: item,
            value: item,
          })) || [],
      },
    },
    {
      component: "CustomMultiSelectV2",
      props: {
        label: "Vehicle Function",
        required: true,
        infoname: "vehicle_function",
        selectedValues: vehicleFunctions,
        setSelectedValues: setVehicleFunctions,
        size: "small",
        optionsList:
          projectDetailsSelected?.vehicle_function_details?.map((item) => ({
            label: `${item.name} (${item.abbreviation})`,
            value: item?.id,
          })) || [],
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select PowertrainClass",
        label: "Powertrain Class",
        infoname: "power_train_class",
        required: true,
        selectedValues: powertrainClasses,
        setSelectedValues: setPowertrainClasses,
        selectedValue: powertrainClasses,
        handleOnChange: (e) => setPowertrainClasses(e.target.value),
        size: "small",
        optionsList:
          projectDetailsSelected?.powertrain_class_details?.map((item) => ({
            label: `${item.name} (${item.abbreviation})`,
            value: item?.id,
          })) || [],
      },
    },
    {
      component: "CustomSelectV3",
      props: {
        placeholder: "Select Manufacturer Details",
        label: "Manufacturer Details",
        infoname: "manufacturer_name",
        required: true,
        selectedValue: manufacturerDetails,
        handleOnChange: (e) => {
          console.log(
            "---setManufacturerDetails(e.target.value)---",
            e.target.value,
            projectDetailsSelected?.mft_details,
          );
          setManufacturerDetails(e.target.value);
        },

        optionsList:
          projectDetailsSelected?.mft_details?.map((item) => ({
            label: item?.name,
            subLabel: item?.address,
            value: item,
          })) || [],
      },
    },
    ...(openRecreationVariant
      ? [
        {
          component: "CustomMultiSelectV2",
          props: {
            label: "Co Owners",
            required: false,
            selectedValues: coOwners,
            setSelectedValues: setCoOwners,
            optionsList:
              Array.isArray(usersListData) && usersListData.length > 0
                ? usersListData.map((item) => ({
                  label: item.fullname,
                  subLabel: item.email,
                  value: item?.user_id,
                }))
                : [],
            renderOption: (props, option) => (
              <li {...props} key={option.value}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontWeight: 400 }}>
                    {option.label}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                  >
                    {option.subLabel}
                  </Typography>
                </Box>
              </li>
            ),
            getOptionLabel: (option) => option.label,
          },
        },
        {
          component: "CustomMultiSelectV2",
          props: {
            label: "Editor",
            required: false,
            selectedValues: editors,
            setSelectedValues: setEditors,
            optionsList:
              Array.isArray(usersListData) && usersListData.length > 0
                ? usersListData.map((item) => ({
                  label: item.fullname,
                  subLabel: item.email,
                  value: item?.user_id,
                }))
                : [],
            renderOption: (props, option) => (
              <li {...props} key={option.value}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontWeight: 400 }}>
                    {option.label}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                  >
                    {option.subLabel}
                  </Typography>
                </Box>
              </li>
            ),
            getOptionLabel: (option) => option.label,
          },
        },
        {
          component: "CustomMultiSelectV2",
          props: {
            label: "Viewer",
            required: false,
            selectedValues: viewers,
            setSelectedValues: setViewers,
            optionsList:
              Array.isArray(usersListData) && usersListData.length > 0
                ? usersListData.map((item) => ({
                  label: item.fullname,
                  subLabel: item.email,
                  value: item?.user_id,
                }))
                : [],
            renderOption: (props, option) => (
              <li {...props} key={option.value}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontWeight: 400 }}>
                    {option.label}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                  >
                    {option.subLabel}
                  </Typography>
                </Box>
              </li>
            ),
            getOptionLabel: (option) => option.label,
          },
        },
      ]
      : []),
  ];

  const singleColumnRows = [1, 2, 4, 5, 6, 7, 9];

  const updateMilestoneFormLayout = [
    {
      component: "MilestoneSection",
      props: getMilestoneSectionProps({
        rows: updateMilestoneRows,
        draft: updateMilestoneDraft,
        setRows: setUpdateMilestoneRows,
        setDraft: setUpdateMilestoneDraft,
      }),
    },
  ];

  const handleExpandPC = (value) => {
    setExpandPC(value);
    if (value) setExpandVC(false);
    else setExpandVC(true);
    handleVariantToggle(0, variantDetailsSelected?.[0]);
  };

  const handleExpandVC = (value) => {
    setExpandVC(value);
    // handleVariantToggle(0, variantDetailsSelected?.[0]);
    if (value) {
      setExpandPC(false);
      handleVariantToggle(0, variantDetailsSelected?.[0]);
    } else {
      setExpandPC(true);
    }
  };

  const handleBackToPC = () => {
    setExpandPC(true);
    setExpandVC(false);
  };

  const handleExpandPCLogs = (value) => {
    setExpandPCLogs(value);
  };
  const handleExpandVCLogs = (value) => {
    setExpandVCLogs(value);
  };

  // 🔹 Toggles which inner variant is open
  const handleVariantToggle = (index, variant) => {
    setExpandedIndex((prev) => {
      const newIndex = prev === index ? null : index;

      if (newIndex === null) {
        setExpandPC(true);
        setExpandVC(false);
      } else {
        setExpandPC(false);
      }

      return newIndex;
    });

    setSelectedVariantDetails(variant);
  };

  const handleAddVariant = (e) => {
    e.stopPropagation();
    setOpenAddVariant(true);
  };

  const handleOpenCloseAddVariant = (value) => {
    setOpenAddVariant(value);
    setOpenRecreationVariant(value);
    if (!value) {
      setVariantName("");
      setModelYear(null);
      setVariantColor("#000000");
      setDeliveryMarkets(null);
      setVehicleFunctions([]);
      setPowertrainClasses(null);
      setManufacturerDetails(null);
      setMilestoneRows([]);
      setMilestoneDraft(createMilestoneRow());
    }
  };

  const handleOpenUpdateMilestoneDialog = (target = "project") => {
    const milestoneSource =
      target === "variant"
        ? selectedVariantDetails?.milestones || projectDetailsSelected?.milestones
        : projectDetailsSelected?.milestones;

    setUpdateMilestoneTarget(target);
    setUpdateMilestoneRows(
      mapProjectMilestonesToRows(milestoneSource),
    );
    setUpdateMilestoneDraft(createMilestoneRow());
    setOpenUpdateMilestoneDialog(true);
  };

  const handleCloseUpdateMilestoneDialog = () => {
    setOpenUpdateMilestoneDialog(false);
    setUpdateMilestoneTarget("project");
    setUpdateMilestoneRows([]);
    setUpdateMilestoneDraft(createMilestoneRow());
  };

  const fetchInitialData = async () => {
    try {
      const [usersList] = await Promise.all([
        dispatch(
          HandleApiActions({
            ...projectConfigAPI.get_users_list,
            params: {
              page_size: 1000,
              page_number: 1,
            },
          }),
        ),
      ]);

      setUsersListData(usersList?.data || []);
      setFullUsersList(usersList?.data || []);
    } catch (error) {
      console.error("❌ Failed to load initial data:", error);
    }
  };

  useEffect(() => {
    openRecreationVariant && fetchInitialData();
  }, [openRecreationVariant]);

  useEffect(() => {
    if (openAddVariant && !openRecreationVariant) {
      setMilestoneRows(
        mapProjectMilestonesToRows(projectDetailsSelected?.milestones),
      );
      setMilestoneDraft(createMilestoneRow());
    }
  }, [openAddVariant, openRecreationVariant, projectDetailsSelected?.milestones]);

  useEffect(() => {
    if (
      milestoneDraft.name &&
      milestoneRows.some((milestone) => milestone.name === milestoneDraft.name)
    ) {
      setMilestoneDraft((prev) => ({
        ...prev,
        name: "",
      }));
    }
  }, [milestoneDraft.name, milestoneRows]);

  useEffect(() => {
    console.log("---form users---", coOwners, editors, viewers);
    console.log("---userData---", fullUsersList);

    if (!Array.isArray(fullUsersList)) return;

    // Get all selected user IDs
    const selectedUsers = [...coOwners, ...editors, ...viewers, ...owner].map(
      (user) => user.value,
    );

    // Filter unselected users
    const unSelectedUserListData = fullUsersList.filter(
      (user) => !selectedUsers.includes(user.user_id),
    );

    setUsersListData(unSelectedUserListData);

    console.log("---unSelectedUserListData---", unSelectedUserListData);
  }, [coOwners, editors, viewers, fullUsersList]);

  // functions for recreation
  const setFormStates = (selectedVariant) => {
    // set array to form states
    const parentModelYear = dayjs(selectedVariant?.production_year, "MM-YYYY");

    const parentVehicleFunctionDetails =
      selectedVariant?.vehicle_function_details?.map((item) => ({
        label: `${item.name} (${item.abbreviation})`,
        value: item?.id,
      })) || [];

    const parentCoOwnerDetails = selectedVariant?.co_owner_details?.[0].user_id
      ? selectedVariant?.co_owner_details?.map((item) => {
        return {
          label: item.name,
          subLabel: item.email,
          value: item?.user_id,
        };
      })
      : [];

    const parentEditUserDetails = selectedVariant?.edit_user_details?.[0]
      ?.user_id
      ? selectedVariant?.edit_user_details?.map((item) => {
        return {
          label: item.name,
          subLabel: item.email,
          value: item?.user_id,
        };
      })
      : [];

    const parentViewUserDetails = selectedVariant?.view_user_details?.[0]?.user_id
      ? selectedVariant?.view_user_details?.map((item) => {
        return {
          label: item.name,
          subLabel: item.email,
          value: item?.user_id,
        };
      })
      : [];

    setVariantName(selectedVariant?.name || "");
    setModelYear(parentModelYear || null);
    setVariantColor(selectedVariant?.color_code || "#000000");
    setDeliveryMarkets(selectedVariant?.delivery_market[0] || null);
    setVehicleFunctions(parentVehicleFunctionDetails || []);
    setPowertrainClasses(Number(selectedVariant?.pwrtrn_class) || null);
    setCoOwners(parentCoOwnerDetails || []);
    setEditors(parentEditUserDetails || []);
    setViewers(parentViewUserDetails || []);
    setMilestoneRows(
      mapProjectMilestonesToRows(
        selectedVariant?.milestones || projectDetailsSelected?.milestones,
      ),
    );
    setMilestoneDraft(createMilestoneRow());

    // setManufacturerDetails(selectedVariant?.mft_details[0] || null);
    if (
      projectDetailsSelected?.mft_details?.length &&
      selectedVariant?.mft_details?.length
    ) {
      const matchedManufacturer = projectDetailsSelected.mft_details.find(
        (item) => item.name === selectedVariant.mft_details[0]?.name,
      );

      setManufacturerDetails(matchedManufacturer || null);
    } else {
      setManufacturerDetails(null);
    }
  };

  const handleRecreateVariantClick = async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...projectConfigAPI.get_variants_for_recreation,
        params: {
          project_id: projectDetailsSelected.project_id,
          parent_project_id: projectDetailsSelected.parent_project_id,
        },
      }),
    );
    if (!resp.error) {
      setRecreationVariantList(resp?.data);
      // by default select first varient from array

      setSelectedVariantForRecreation(resp?.data?.[0]);

      setFormStates(resp?.data?.[0]);
      setOpenRecreationVariant(true);
    } else {
      showToastMessage({
        type: "error",
        message: resp.msg,
      });
    }
  };

  const handleCloseRecreateVariant = (value) => {
    setOpenRecreationVariant(value);
  };

  const handleClickRecreateDialogVariant = ({ draft = false }) => {
    let reqObj = {
      project_id: parseInt(projectDetailsSelected?.project_id),
      name: variantName,
      color_code: variantColor,
      variant_status: selectedVariantForRecreation?.status,
      save_as_draft: draft,
      delivery_market: deliveryMarkets,
      pwrtrn_class: powertrainClasses,
      ...(vehicleFunctions &&
        vehicleFunctions?.length > 0 && {
        veh_funcs: vehicleFunctions?.map((data) => data?.value),
      }),
      production_year: modelYear?.format("MM-YYYY"),
      current_time: moment().format(),
      mft_details: manufacturerDetails,
      milestones: buildMilestonesPayload(),
      parent_variant_id: selectedVariantForRecreation?.variant_id,
      parent_project_id: projectDetailsSelected.parent_project_id,
      ...(coOwners?.length > 0
        ? {
          co_owners: coOwners?.map((data) => data?.value),
        }
        : null),
      ...(editors?.length > 0
        ? {
          edit_users: editors?.map((data) => data?.value),
        }
        : null),
      ...(viewers?.length > 0
        ? {
          view_users: viewers?.map((data) => data?.value),
        }
        : null),
    };
    console.log("VarisntreqObj", reqObj);

    dispatch(
      HandleApiActions({
        ...projectConfigAPI.add_recreated_variant,
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      fetchProjectAndVariantDetails();
    });

    handleOpenCloseAddVariant(false);
  };

  const buildMilestonesPayload = (rows = milestoneRows) =>
    rows
      .slice()
      .sort(
        (a, b) =>
          (milestoneNameOptions.find((option) => option.value === a.name)
            ?.sequence ?? Number.MAX_SAFE_INTEGER) -
            (milestoneNameOptions.find((option) => option.value === b.name)
              ?.sequence ?? Number.MAX_SAFE_INTEGER) ||
          a.name.localeCompare(b.name),
      )
      .map((milestone) => ({
        name: milestone.name?.trim(),
        milestone_date: milestone.milestoneDate?.format("MM-YYYY"),
        status: true,
      }))
      .filter((milestone) => milestone.name && milestone.milestone_date);

  const normalizeMilestonesForCompare = (milestones = []) =>
    milestones
      .map((milestone) => ({
        name: milestone?.name?.trim(),
        milestone_date:
          parseMilestoneDate(milestone?.milestone_date)?.format("MM-YYYY") ||
          milestone?.milestone_date,
        status: milestone?.status ?? true,
      }))
      .filter((milestone) => milestone.name && milestone.milestone_date)
      .sort(
        (a, b) =>
          (milestoneNameOptions.find((option) => option.value === a.name)
            ?.sequence ?? Number.MAX_SAFE_INTEGER) -
            (milestoneNameOptions.find((option) => option.value === b.name)
              ?.sequence ?? Number.MAX_SAFE_INTEGER) ||
          a.name.localeCompare(b.name),
      );

  const originalMilestonePayload = normalizeMilestonesForCompare(
    updateMilestoneTarget === "variant"
      ? selectedVariantDetails?.milestones || projectDetailsSelected?.milestones
      : projectDetailsSelected?.milestones,
  );
  const updateMilestonePayload = buildMilestonesPayload(updateMilestoneRows);
  const hasMilestoneChanges =
    JSON.stringify(originalMilestonePayload) !==
    JSON.stringify(updateMilestonePayload);

  const hasInvalidMilestones =
    (openAddVariant || openRecreationVariant) &&
    (milestoneRows.length !== milestoneNameOptions.length ||
      milestoneRows.some(
        (milestone) =>
          milestone.name?.trim().length < 3 || !milestone.milestoneDate,
      ));

  const isAddDisabledVC =
    variantName.trim().length < 3 ||
    !vehicleFunctions?.length ||
    !modelYear ||
    !deliveryMarkets ||
    !powertrainClasses ||
    !manufacturerDetails ||
    hasInvalidMilestones;

  const isUpdateMilestoneDisabled =
    updateMilestoneRows.length !== milestoneNameOptions.length ||
    updateMilestoneRows.some(
      (milestone) =>
        milestone.name?.trim().length < 3 || !milestone.milestoneDate,
    ) ||
    !hasMilestoneChanges;

  const handleUpdateMilestones = async () => {
    const currentVariantId = selectedVariantDetails?.variant_id;
    const reqObj = {
      project_id: parseInt(projectDetailsSelected?.project_id),
      ...(updateMilestoneTarget === "variant" && {
        variant_id: parseInt(currentVariantId),
      }),
      current_time: moment().toISOString(),
      milestones: buildMilestonesPayload(updateMilestoneRows),
    };

    const resp = await dispatch(
      HandleApiActions({
        ...(updateMilestoneTarget === "variant"
          ? projectConfigAPI.update_variant_milestones
          : projectConfigAPI.update_project_milestones),
        params: reqObj,
        show_toast: true,
      }),
    );

    if (!resp?.error) {
      const projectVariantResp = await fetchProjectAndVariantDetails();

      if (updateMilestoneTarget === "variant") {
        const updatedVariant =
          projectVariantResp?.variantData?.find(
            (variant) => variant.variant_id === currentVariantId,
          ) || null;

        setSelectedVariantDetails(
          updatedVariant || {
            ...selectedVariantDetails,
            milestones: reqObj.milestones,
          },
        );
      }

      handleCloseUpdateMilestoneDialog();
    }
  };

  const handleProjectLogs = async ({ project_id, page_number, page_size }) => {
    try {
      const queryParams = { project_id, page_number, page_size };
      const resp = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_project_change_logs,
          params: queryParams,
        }),
      );
      return resp;
    } catch (error) {
      console.error("❌ Error fetching project logs:", error.message);
      throw error;
    }
  };

  const handleVariantLogs = async ({
    project_id,
    page_number,
    page_size,
    variant_id,
  }) => {
    try {
      const queryParams = { project_id, page_number, page_size, variant_id };
      const resp = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.get_variant_change_logs,
          params: queryParams,
        }),
      );
      return resp;
    } catch (error) {
      console.error("❌ Error fetching project logs:", error.message);
      throw error;
    }
  };

  const fetchLogsProject = async () => {
    setLoading(true);
    try {
      const response = await handleProjectLogs({
        project_id: projectDetailsSelected?.project_id,
        page_number: pagePCLogCount,
        page_size: rowsPerPagePCLogCount,
      });
      console.log("---response---response", response);

      setLogsProject(response?.data || []);
      setLogsCount(response?.total_count);
    } catch (error) {
      console.error("Failed to load logs:", error);
    } finally {
      setLoading(false);
    }
  };
  console.log("logsCount", logsCount);

  const handlePageChangeLogs = async (newPage) => {
    setPagePCLogCount(newPage);
  };
  const handlePageChangeLogsVC = async (newPage) => {
    setPageVCLogCount(newPage);
  };

  const fetchLogsVariant = async () => {
    setLoading(true);
    try {
      const response = await handleVariantLogs({
        project_id: projectDetailsSelected?.project_id,
        variant_id: selectedVariantDetails?.variant_id,
        page_number: pageVCLogCount,
        page_size: rowsPerPageVCLogCount,
      });
      console.log("---response---response", response);

      setLogsVariant(response?.data || []);
      console.log("--Variant Logs REsponse", response?.total_count);
      setLogsCountVC(response?.total_count);
    } catch (error) {
      console.error("Failed to load logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleStatusSettings = async ({ page_number, page_size }) => {
    try {
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI?.get_vehicle_status,
          params: { page_number, page_size },
        }),
      );
      console.log("--Vehicle Status Resp---", resp);
      setVehicleStatusData(Array.isArray(resp?.data) ? resp.data : []);

      return resp;
    } catch (error) {
      console.error("❌ Failed to load vehicle status:", error);
    }
  };
  useEffect(() => {
    fetchLogsProject();
  }, [pagePCLogCount, rowsPerPagePCLogCount]);

  useEffect(() => {
    fetchLogsVariant();
  }, [pageVCLogCount, rowsPerPageVCLogCount]);

  useEffect(() => {
    fetchVehicleStatusSettings({ page_number: 1, page_size: 1000 });
  }, [projectDetailsSelected, variantDetailsSelected]);

  // recreate useEffect
  useEffect(() => {
    setOwner(selectedVariantForRecreation?.owner_details?.[0]?.user_id || "");
  }, [selectedVariantForRecreation]);

  // ✅ useEffect to load logs when project changes
  useEffect(() => {
    if (projectDetailsSelected) {
      fetchLogsProject();
    }

    if (selectedVariantDetails) {
      fetchLogsVariant();
    }
  }, [projectDetailsSelected, selectedVariantDetails]);

  const tabsDataUsersPC = buildTabsDataUsersPC(projectDetailsSelected);

  // const tabsDataUsersVC = [
  //   {
  //     name: "Owner",
  //     count:
  //       selectedVariantDetails?.owner_details?.filter(
  //         (u) => Object.keys(u || {}).length > 0,
  //       )?.length || 0,
  //     content: (
  //       <Table
  //         stickyHeader
  //         size="small"
  //         sx={{
  //           tableLayout: "fixed",
  //           width: "100%",
  //         }}
  //       >
  //         <TableBody>
  //           {selectedVariantDetails?.owner_details?.filter(
  //             (u) => Object.keys(u || {}).length > 0,
  //           )?.length > 0 ? (
  //             selectedVariantDetails?.owner_details
  //               ?.filter((u) => Object.keys(u || {}).length > 0)
  //               ?.map((col) => (
  //                 <TableRow key={col.user_id}>
  //                   <TableCell sx={{ width: col.width || "150px" }}>
  //                     {`${col?.name ?? "-"} - ${col?.email ?? "-"}`}
  //                   </TableCell>
  //                 </TableRow>
  //               ))
  //           ) : (
  //             <TableRow>
  //               <TableCell align="center" sx={{ py: 2 }}>
  //                 No data available
  //               </TableCell>
  //             </TableRow>
  //           )}
  //         </TableBody>
  //       </Table>
  //     ),
  //   },
  //   {
  //     name: "Editor",
  //     count:
  //       (selectedVariantDetails?.co_owner_details?.filter(
  //         (u) => Object.keys(u || {}).length > 0,
  //       )?.length || 0) +
  //       (selectedVariantDetails?.edit_user_details?.filter(
  //         (u) => Object.keys(u || {}).length > 0,
  //       )?.length || 0),

  //     content: (
  //       <Table
  //         stickyHeader
  //         size="small"
  //         sx={{
  //           tableLayout: "fixed",
  //           width: "100%",
  //         }}
  //       >
  //         <TableBody>
  //           {/* merge two arrays */}
  //           {[
  //             ...(selectedVariantDetails?.co_owner_details?.filter(
  //               (u) => Object.keys(u || {}).length > 0,
  //             ) || []),
  //             ...(selectedVariantDetails?.edit_user_details?.filter(
  //               (u) => Object.keys(u || {}).length > 0,
  //             ) || []),
  //           ]?.length > 0 ? (
  //             [
  //               ...(selectedVariantDetails?.co_owner_details?.filter(
  //                 (u) => Object.keys(u || {}).length > 0,
  //               ) || []),
  //               ...(selectedVariantDetails?.edit_user_details?.filter(
  //                 (u) => Object.keys(u || {}).length > 0,
  //               ) || []),
  //             ]?.map((user) => (
  //               <TableRow key={user?.user_id}>
  //                 <TableCell sx={{ width: user?.width || "200px" }}>
  //                   {`${user?.name ?? "-"} - ${user?.email ?? "-"}`}
  //                 </TableCell>
  //               </TableRow>
  //             ))
  //           ) : (
  //             <TableRow>
  //               <TableCell align="center" sx={{ py: 2 }}>
  //                 No data available
  //               </TableCell>
  //             </TableRow>
  //           )}
  //         </TableBody>
  //       </Table>
  //     ),
  //   },
  //   {
  //     name: "Viewer",
  //     count:
  //       selectedVariantDetails?.view_user_details?.filter(
  //         (u) => Object.keys(u || {}).length > 0,
  //       )?.length || 0,
  //     content: (
  //       <Table
  //         stickyHeader
  //         size="small"
  //         sx={{
  //           tableLayout: "fixed",
  //           width: "100%",
  //         }}
  //       >
  //         <TableBody>
  //           {selectedVariantDetails?.view_user_details?.filter(
  //             (u) => Object.keys(u || {}).length > 0,
  //           )?.length > 0 ? (
  //             selectedVariantDetails?.view_user_details
  //               ?.filter((u) => Object.keys(u || {}).length > 0)
  //               ?.map((col) => (
  //                 <TableRow key={col.user_id}>
  //                   <TableCell sx={{ width: col.width || "150px" }}>
  //                     {`${col?.name ?? "-"} - ${col?.email ?? "-"}`}
  //                   </TableCell>
  //                 </TableRow>
  //               ))
  //           ) : (
  //             <TableRow>
  //               <TableCell align="center" sx={{ py: 2 }}>
  //                 No data available
  //               </TableCell>
  //             </TableRow>
  //           )}
  //         </TableBody>
  //       </Table>
  //     ),
  //   },
  // ];
  const tabsDataUsersVC = buildTabsDataUsersVC(selectedVariantDetails);
  const projectDetails = [
    {
      label: "Project Status",
      value: Object.keys(metaData?.project_status || {}).find(
        (key) =>
          metaData?.project_status?.[key] === projectDetailsSelected?.status,
      ),
      editable: true,
      optionList:
        Object?.entries(metaData?.project_status || {})?.map(
          ([key, value]) => ({
            label: key.replace(/_/g, " "),
            value: value,
          }),
        ) || [],
    },
    { label: "Architecture", value: projectDetailsSelected?.architecture_name },
    {
      label: "Trademark",
      value: projectDetailsSelected?.trade_mark,
      editable: true,
    },
    {
      label: "Vehicle Platform",
      value: projectDetailsSelected?.vehicle_platform,
    },
    {
      label: "Vehicle Class",
      value: `${projectDetailsSelected?.vehicle_class_details?.[0]?.class || ""
        } - ${projectDetailsSelected?.vehicle_class_details?.[0]?.sub_class || ""
        }`,
    },
    {
      label: "Vehicle Name",
      value: `${projectDetailsSelected?.vehicle_class_details?.[0]?.name || ""
        }`,
    },
    { label: "No of Seats", value: projectDetailsSelected?.no_of_seats },
    { label: "Target Market", value: projectDetailsSelected?.target_market },
    {
      label: "Created At",
      value: dayjs(projectDetailsSelected?.created_at).format(
        "DD MMM YYYY, hh:mm A",
      ),
    },
    { label: "Model Year", value: projectDetailsSelected?.model_year },
  ];

  const getProjectMilestoneSequence = (milestone, index) =>
    milestone?.sequence ??
    vehicleStatusList.find((item) => item?.name === milestone?.name)
      ?.sequence ??
    index;

  const projectMilestoneDetailsRows =
    projectDetailsSelected?.milestones
      ?.slice()
      .sort(
        (a, b) =>
          getProjectMilestoneSequence(
            a,
            projectDetailsSelected?.milestones?.indexOf(a),
          ) -
            getProjectMilestoneSequence(
              b,
              projectDetailsSelected?.milestones?.indexOf(b),
            ) ||
          (a?.name || "").localeCompare(b?.name || ""),
      )
      .map((data) => ({
        milestone_name: data?.name,
        milestone_date:
          parseMilestoneDate(data?.milestone_date)?.format("MM-YYYY") ||
          data?.milestone_date,
      })) || [];

  const buildMilestoneDetailsRows = (milestones = []) =>
    milestones
      ?.slice()
      .sort(
        (a, b) =>
          getProjectMilestoneSequence(a, milestones?.indexOf(a)) -
            getProjectMilestoneSequence(b, milestones?.indexOf(b)) ||
          (a?.name || "").localeCompare(b?.name || ""),
      )
      .map((data) => ({
        milestone_name: data?.name,
        milestone_date:
          parseMilestoneDate(data?.milestone_date)?.format("MM-YYYY") ||
          data?.milestone_date,
      })) || [];

  const tableProjectConfigs = [
    {
      key: "zones",
      condition: projectDetailsSelected?.is_zonal,
      title: "Zones",
      count: projectDetailsSelected?.project_zones?.length || 0,
      columns: [
        { id: "zone_position", label: "Zone Position" },
        { id: "zone_name", label: "Zone Name" },
      ],
      rows:
        projectDetailsSelected?.project_zones?.map((data) => ({
          zone_position: data?.position,
          zone_name: data?.zone_name,
        })) || [],
      handleOnClick: (title) => {
        console.log("Clicked:", title);
        setOpenDetailsDialog(true);
        setDialogTitleProject(title?.toUpperCase());
      },
    },
    {
      key: "vehicleFunction",
      title: "Vehicle Function(Project)",
      count: projectDetailsSelected?.vehicle_function_details?.length || 0,
      columns: [
        { id: "vehicle_function_name", label: "Vehicle Function Name" },
        { id: "description", label: "Description" },
      ],
      rows:
        projectDetailsSelected?.vehicle_function_details?.map((data) => ({
          vehicle_function_name: data?.name,
          description: data?.other_fields?.description,
        })) || [],
      handleOnClick: (title) => {
        console.log("Clicked:", title);
        setOpenDetailsDialog(true);
        setDialogTitleProject(title?.toUpperCase());
      },
    },
    {
      key: "powerTrain",
      title: "Powertrain Class",
      count: projectDetailsSelected?.powertrain_class_details?.length || 0,
      columns: [
        { id: "powertrain_name", label: "Powertrain Name" },
        { id: "description", label: "Description" },
      ],
      rows:
        projectDetailsSelected?.powertrain_class_details?.map((data) => ({
          powertrain_name: data?.name,
          description: data?.other_fields?.description,
        })) || [],
      handleOnClick: (title) => {
        console.log("Clicked:", title);
        setOpenDetailsDialog(true);
        setDialogTitleProject(title?.toUpperCase());
      },
    },
    {
      key: "deliveryMarket",
      title: "Delivery Market",
      count: projectDetailsSelected?.delivery_market?.length || 0,
      columns: [{ id: "delivery_market", label: "Delivery Market" }],
      rows:
        projectDetailsSelected?.delivery_market?.map((data) => ({
          delivery_market: data,
        })) || [],
      handleOnClick: (title) => {
        console.log("Clicked:", title);
        setOpenDetailsDialog(true);
        setDialogTitleProject(title?.toUpperCase());
      },
    },
    {
      key: "manufacturerDetails",
      title: "Manufacturer Configurations",
      count: projectDetailsSelected?.mft_details?.length || 0,
      columns: [
        { id: "manufacturer_name", label: "Manufacturer Name" },
        { id: "address", label: "Address" },
      ],
      rows:
        projectDetailsSelected?.mft_details?.map((data) => ({
          manufacturer_name: data?.name,
          address: data?.address,
        })) || [],
      handleOnClick: (title) => {
        console.log("Clicked:", title);
        setOpenDetailsDialog(true);
        setDialogTitleProject(title?.toUpperCase());
      },
    },
    {
      key: "milestoneDetails",
      title: "Milestone Details",
      count: projectMilestoneDetailsRows.length,
      columns: [
        { id: "milestone_name", label: "Milestone Name" },
        { id: "milestone_date", label: "Milestone Date" },
      ],
      rows: projectMilestoneDetailsRows,
      actionIcon: "edit",
      handleOnClick: handleOpenUpdateMilestoneDialog,
    },
    {
      key: "users",
      title: "Users",
      onlyUsers: <CustomTabsV2 tabs={tabsDataUsersPC} />,
    },
  ];

  const isVehicleFunctionVariant = {
    title: "Vehicle Function(Variant)",
    count: selectedVariantDetails?.vehicle_function_details?.length || 0,
    columns: [
      { id: "vehicle_function_name", label: "Vehicle Function Name" },
      { id: "description", label: "Description" },
    ],
    rows:
      selectedVariantDetails?.vehicle_function_details?.map((data) => ({
        vehicle_function_name: data?.name,
        description: data?.other_fields?.description,
      })) || [],
    handleOnClick: (title) => {
      console.log("Clicked:", title);
      setOpenDetailsDialog(true);
      setDialogTitleProject(title?.toUpperCase());
    },
  };
  const variantMilestoneDetailsRows = buildMilestoneDetailsRows(
    selectedVariantDetails?.milestones || projectDetailsSelected?.milestones,
  );
  const isMilestoneDetailsVariant = {
    title: "Milestone Details",
    count: variantMilestoneDetailsRows.length,
    columns: [
      { id: "milestone_name", label: "Milestone Name" },
      { id: "milestone_date", label: "Milestone Date" },
    ],
    rows: variantMilestoneDetailsRows,
    actionIcon: "edit",
    handleOnClick: () => handleOpenUpdateMilestoneDialog("variant"),
  };
  const isVariantUsers = {
    key: "users",
    title: "Users",
    onlyUsers: <CustomTabsV2 tabs={tabsDataUsersVC} />,
  };

  const isVariantZones = {
    title: "Zones",

    count: selectedVariantDetails?.zone_details?.length || 0,
    columns: [
      { id: "zone_position", label: "Zone Position" },
      { id: "zone_name", label: "Zone Name" },
    ],
    rows:
      selectedVariantDetails?.zone_details?.map((data, idx) => ({
        zone_position: data?.zone?.position,
        zone_name: data?.zone?.zone_name,
      })) || [],
    handleOnClick: console.log("Hie"),
  };

  const handleEditClick = async (label, editedValue, variant) => {
    console.log("Edit clicked for:", label?.toUpperCase());
    console.log("editedValueeditedValue", editedValue);
    if (label?.toUpperCase() === "TRADEMARK") {
      handleClickEdit(label?.toUpperCase(), editedValue);
    } else if (label?.toUpperCase() === "PROJECT STATUS") {
      const reqObj = {
        project_id: parseInt(projectDetailsSelected?.project_id),
        status: editedValue,
        current_time: moment().format(),
      };

      const resp = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.update_project_status,
          params: reqObj,
          show_toast: true,
        }),
      );
      if (!resp.error) {
        await handleProjectDetails({
          project_id: projectDetailsSelected?.project_id,
        });

        setProjectDetailsSelected((prev) => ({
          ...prev,
          status: editedValue,
        }));

        setEditSaveIndex(true);
      }
    } else if (label?.toUpperCase() === "VARIANT COLOR") {
      const resp = await dispatch(
        HandleApiActions({
          ...projectConfigAPI.update_variant_color,
          params: {
            project_id: variant?.project_id,
            variant_id: variant?.variant_id,
            color_code: editedValue,
          },
          show_toast: true,
        }),
      );
      if (!resp?.error) {
        await handleVariantDetails({
          project_id: variant?.project_id,
          is_complete_details_required: true,
        });

        setVariantDetailsSelected((prev) =>
          prev.map((item) =>
            item?.variant_id === variant?.variant_id
              ? { ...item, color_code: editedValue }
              : item,
          ),
        );
      }
    } else if (label?.toUpperCase() === "VEHICLE STATUS") {
      const reqObj = {
        project_id: variant?.project_id,
        variant_id: variant?.variant_id,
        vehicle_status: editedValue,
        reason: "want changes",
      };
      const resp = await dispatch(
        HandleApiActions({
          ...projectConfigAPI?.update_vehicle_status,
          params: reqObj,
          show_toast: true,
        }),
      );
      if (!resp?.error) {
        await fetchProjectAndVariantDetails();

        // setSelectedVariantDetails((prev) => {
        //   if (!Array.isArray(prev)) return prev ?? [];

        //   return prev.map((item) => {
        //     if (item?.variant_id === variant?.variant_id) {
        //       return {
        //         ...item,
        //         vehicle_status: editedValue,
        //       };
        //     }
        //     return item;
        //   });
        // });
      }
    }
  };

  console.log("----VariantDetailsSelected-=", variantDetailsSelected);

  const fetchEditRequests = async ({
    project_id,
    variant_id,
    er_id,
    status,
    is_sent_requests,
    ee_id,
    swm_id,
    page_number,
    page_size,
  }) => {
    const apiDetails = editRequestAPI.get_edit_requests;

    // ✅ Mandatory parameter check
    if (!project_id || !variant_id || !page_number || !page_size) {
      console.error("❌ Missing mandatory params in fetchEditRequests:", {
        project_id,
        variant_id,
        page_number,
        page_size,
      });
      return;
    }

    const params = {
      project_id,
      variant_id,
      page_number,
      page_size,
      ...(er_id && { er_id }),
      ...(status && { status }),
      ...(is_sent_requests && { is_sent_requests }),
      ...(ee_id && { ee_id }),
      ...(swm_id && { swm_id }),
    };

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: false,
        params,
      }),
    );

    console.log("---Fetch Edit Requests Resp---", resp);
    if (resp?.total_count > 0) {
      setEditRequestCount(resp?.total_count);
    } else {
      setEditRequestCount("");
    }
    if (resp?.data?.length > 0) {
      setTableRowsDataER(
        resp?.data
          ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          ?.map((item) => ({
            id: item.er_id,

            status: item.status,
            status_data:
              item.status === 100
                ? "Approved"
                : item.status === 200
                  ? "Rejected"
                  : "Pending",
            requested_at: new Date(item.created_at).toLocaleString(),

            requested_user_details: {
              fullname: item.requested_user?.fullname || "-",
              email: item.requested_user?.email || "-",
            },
            requested_reason: item.request_reason || "-",

            approved_rejected_by: item.approved_user?.fullname || "-",
            approved_rejected_reason: item.update_reason || "-",

            ee_details: item.ee_details || null,
            swm_details: item.swm_details || null,
          })),
      );
      return resp;
    } else {
      setTableRowsDataER([]);
    }
  };

  useEffect(() => {
    fetchEditRequests({
      project_id: projectDetailsSelected?.project_id,
      variant_id: selectedVariantDetails?.variant_id,
      page_number: pageER,
      page_size: rowsPerPageER,
    });
  }, [pageER, rowsPerPageER, projectDetailsSelected, selectedVariantDetails]);

  // ✅ Tabs Data
  const tabsDataProject = [
    {
      name: "Details",
      count: "",
      content: (
        <ViewPCDetails
          selectedProjectDetails={projectDetailsSelected}
          projectDetails={projectDetails}
          handleEditClick={handleEditClick}
          editSaveIndex={editSaveIndex}
        />
      ),
    },
    {
      name: "Logs",
      count: logsCount || "",
      content: (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ShowLogsDetails
            page={pagePCLogCount}
            rowsPerPage={rowsPerPagePCLogCount}
            logsCount={logsCount}
            expanded={expandPCLogs}
            setExpanded={handleExpandPCLogs}
            logs={logsProject}
            loading={loading}
            onPageChange={handlePageChangeLogs}
          />
        </LocalizationProvider>
      ),
    },
  ];

  const tabsData1 = [
    {
      name: "Details",
      count: "",
      content: (
        <ShowVariantDetails
          projectDetailsSelected={projectDetailsSelected}
          isProjectZones={isVariantZones}
          isVehicleFunction={isVehicleFunctionVariant}
          isMilestoneDetails={isMilestoneDetailsVariant}
          isVariantUsers={isVariantUsers}
        />
      ),
    },
    {
      name: "Requests",
      count: editRequestCount,
      content: (
        <ShowVarientRequestDetails
          handleRowPerPageER={handleRowPerPageER}
          editRequestCount={editRequestCount}
          fetchEditRequests={fetchEditRequests}
          page={pageER}
          rowsPerPage={rowsPerPageER}
          tableRowsData={tableRowsDataER}
        />
      ),
    },
  ];

  const handleClickAddDialogVariant = () => {
    let reqObj = {
      project_id: parseInt(projectDetailsSelected?.project_id),
      name: variantName,
      color_code: variantColor,
      delivery_market: deliveryMarkets,
      pwrtrn_class: powertrainClasses,
      ...(vehicleFunctions &&
        vehicleFunctions?.length > 0 && {
        veh_funcs: vehicleFunctions?.map((data) => data?.value),
      }),
      production_year: modelYear?.format("MM-YYYY"),
      current_time: moment().format(),
      mft_details: manufacturerDetails,
      milestones: buildMilestonesPayload(),
    };
    console.log("VarisntreqObj", reqObj);

    dispatch(
      HandleApiActions({
        ...projectConfigAPI.add_variant,
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      // dispatch(
      //   HandleApiActions({
      //     ...projectConfigAPI.get_variant_details,
      //     params: {
      //       project_id: projectDetailsSelected?.project_id,
      //       is_complete_details_required: true,
      //     },
      //   })
      // );
      fetchProjectAndVariantDetails();
    });

    handleOpenCloseAddVariant(false);
  };

  const handleClickEdit = async (title, editedValue) => {
    try {
      console.log("Edit Title Update Project and Variant:", title, editedValue);

      // 🔥 Correct condition (no auto-true string)
      const isProjectUpdate =
        title === "TRADEMARK" ||
        title === "VEHICLE FUNCTION(PROJECT)" ||
        title === "POWERTRAIN CLASS" ||
        title === "MANUFACTURER CONFIGURATIONS" ||
        title === "DELIVERY MARKET";

      const isVariantUpdate = title === "VEHICLE FUNCTION(VARIANT)";

      let reqObj = {};

      if (isProjectUpdate) {
        reqObj = {
          project_id: parseInt(projectDetailsSelected?.project_id),

          ...(title === "TRADEMARK" && { trade_mark: editedValue }),

          ...(title === "VEHICLE FUNCTION(PROJECT)" && {
            vehicle_funcs: selectedValuesUpdateProject[title]?.map(
              (d) => d?.value,
            ),
          }),

          ...(title === "POWERTRAIN CLASS" && {
            pwrtrn_class: selectedValuesUpdateProject[title]?.map(
              (d) => d?.value,
            ),
          }),

          ...(title === "MANUFACTURER CONFIGURATIONS" && {
            mft_details: selectedValuesUpdateProject[title],
          }),

          ...(title === "DELIVERY MARKET" && {
            delivery_market: selectedValuesUpdateProject[title],
          }),

          current_time: moment().format(),
        };

        console.log("📤 Project Update Request:", reqObj);

        const resp = await dispatch(
          HandleApiActions({
            ...projectConfigAPI.update_project,
            params: reqObj,
            show_toast: true,
          }),
        );

        if (!resp?.error) {
          // Update UI values
          await fetchProjectAndVariantDetails();
          setProjectDetailsSelected((prev) => {
            switch (title) {
              case "TRADEMARK":
                return { ...prev, trade_mark: editedValue };
              case "VEHICLE FUNCTION(PROJECT)":
                return {
                  ...prev,
                  vehicle_funcs: selectedValuesUpdateProject[title]?.map(
                    (d) => d?.value,
                  ),
                };
              case "POWER TRAIN CLASSES":
                return {
                  ...prev,
                  pwrtrn_class: selectedValuesUpdateProject[title]?.map(
                    (d) => d?.value,
                  ),
                };
              // case "MANUFACTURER CONFIGURATIONS":
              //   return {
              //     ...prev,
              //     mft_details: selectedValuesUpdateProject[title],
              //   };
              // case "DELIVERY MARKET":
              //   return {
              //     ...prev,
              //     delivery_market: selectedValuesUpdateProject[title],
              //   };
              default:
                return prev;
            }
          });

          setOpenDetailsDialog(false);
          console.log("✅ Project updated successfully");
        }
      } else if (isVariantUpdate) {
        reqObj = {
          project_id: parseInt(selectedVariantDetails?.project_id),
          variant_id: parseInt(selectedVariantDetails?.variant_id),
          vehicle_functions: selectedValuesUpdateProject[title]?.map(
            (d) => d?.value,
          ),
        };
        console.log("---- reqObj Variant Vehicle Fnction---", reqObj);

        const resp = await dispatch(
          HandleApiActions({
            ...projectConfigAPI.update_vehicle_function,
            params: reqObj,
            show_toast: true,
          }),
        );
        if (!resp?.error) {
          let projectVariantResp = await fetchProjectAndVariantDetails();

          console.log(
            "---projectVariantResp---",
            projectVariantResp.variantData,
          );

          // setSelectedVariantDetails((prev) =>
          //   prev.map((item) => {
          //     if (
          //       item.variant_id === reqObj.variant_id
          //     ) {
          //       const updated = {
          //         ...item,
          //         veh_funcs:
          //           selectedValuesUpdateProject[title]?.map((d) => d?.value) ||
          //           [],
          //       };

          //       console.log("🔹 Updated Variant Object:", updated); // <-- Here is your console log

          //       return updated;
          //     }

          //     return item;
          //   })
          // );

          // setVariantDetailsSelected(projectVariantResp.variantData);

          setSelectedVariantDetails(
            projectVariantResp?.variantData?.find(
              (v) => v.variant_id === selectedVariantDetails.variant_id,
            ) || null,
          );

          setOpenDetailsDialog(false);
        }
      }
    } catch (error) {
      console.error("❌ Error in handleClickEdit:", error);
    }
  };
  useEffect(() => {
    // Export project details
    let filteredProjectDetails = [projectDetailsSelected]?.map((pr) => ({
      name: pr?.name || "",
      trade_mark: pr?.trade_mark || "",
      vehicle_platform: pr?.vehicle_platform || "",
      status: pr?.status || "",
      architecture_name: pr?.architecture_name || "",
      manufacture_details: pr?.mft_details || [],
      ...(pr?.is_zonal ? { zones: pr.project_zones } : {}),
      vehicle_class_details:
        pr?.vehicle_class_details?.map((vc) => ({
          id: vc?.id,
          name: vc?.name,
        }))?.[0] || null,
      powertrain_class_details:
        pr?.powertrain_class_details?.map((pc) => ({
          id: pc?.id,
          name: pc?.name,
        })) || [],
      vehicle_function_details:
        pr?.vehicle_function_details?.map((vf) => ({
          id: vf?.id,
          name: vf?.name,
        })) || [],
      country: pr?.country || "",
      region: pr?.region || "",
      delivery_market: pr?.delivery_market || [],
      target_market: pr?.target_market || "",
      vehicle_type: pr?.vehicle_type || "",
      no_of_seats: pr?.no_of_seats || "",
      model_year: pr?.model_year || "",
      co_owner_details:
        pr?.co_owner_details
          ?.filter((us) => Object.keys(us)?.length > 0)
          ?.map((us) => ({
            user_id: us?.user_id,
            name: us?.name,
            email: us?.email,
          })) || [],
      edit_user_details:
        pr?.edit_user_details
          ?.filter((us) => Object.keys(us)?.length > 0)
          ?.map((us) => ({
            user_id: us?.user_id,
            name: us?.name,
            email: us?.email,
          })) || [],
      view_user_details:
        pr?.view_user_details
          ?.filter((us) => Object.keys(us)?.length > 0)
          ?.map((us) => ({
            user_id: us?.user_id,
            name: us?.name,
            email: us?.email,
          })) || [],
    }));
    setExportProjectDetails(filteredProjectDetails);

    // Export project variants details
    let filteredVariantDetails = Array.isArray(variantDetailsSelected)
      ? variantDetailsSelected
        .filter((vr) => vr && typeof vr === "object") //validate object exists
        .map((vr) => ({
          name: typeof vr?.name === "string" ? vr.name.trim() : "",
          color_code: typeof vr?.color_code === "string" ? vr.color_code : "",
          delivery_market:
            typeof vr?.delivery_market === "string" ? vr.delivery_market : "",

          powertrain_class_details:
            Array.isArray(vr?.powertrain_class_details) &&
              vr.powertrain_class_details.length > 0 &&
              vr.powertrain_class_details[0]?.id
              ? {
                id: vr.powertrain_class_details[0].id,
                name: vr.powertrain_class_details[0]?.name || "",
              }
              : null,

          vehicle_status:
            typeof vr?.vehicle_status === "string" ? vr.vehicle_status : "",

          vehicle_function_details: Array.isArray(
            vr?.vehicle_function_details,
          )
            ? vr.vehicle_function_details
              .filter((vf) => vf?.id)
              .map((vf) => ({
                id: vf.id,
                name: vf?.name || "",
              }))
            : [],

          manufacture_details:
            vr?.mft_details && Object.keys(vr.mft_details)?.length
              ? vr.mft_details
              : null,

          co_owner_details: Array.isArray(vr?.co_owner_details)
            ? vr.co_owner_details
              .filter((us) => us && Object.keys(us).length > 0)
              .map((us) => ({
                user_id: us?.user_id || null,
                name: us?.name || "",
                email: us?.email || "",
              }))
            : [],

          edit_user_details: Array.isArray(vr?.edit_user_details)
            ? vr.edit_user_details
              .filter((us) => us && Object.keys(us).length > 0)
              .map((us) => ({
                user_id: us?.user_id || null,
                name: us?.name || "",
                email: us?.email || "",
              }))
            : [],

          view_user_details: Array.isArray(vr?.view_user_details)
            ? vr.view_user_details
              .filter((us) => us && Object.keys(us).length > 0)
              .map((us) => ({
                user_id: us?.user_id || null,
                name: us?.name || "",
                email: us?.email || "",
              }))
            : [],

          production_year: vr?.production_year || null,
        }))
      : [];
    setExportProjectVariantsDetails(filteredVariantDetails);
  }, [projectDetailsSelected, variantInfo]);

  return (
    <Box sx={{ p: 1 }}>
      {/* 🔹 Header Section */}
      <ProjectConfigHeader
        onBack={() => navigate("/dashboard/project-config")}
        exportProjectDetails={exportProjectDetails}
        exportProjectVariantsDetails={exportProjectVariantsDetails}
      />
      <ProjectConfigMainLayout
        tabsDataProject={tabsDataProject}
        expandPC={expandPC}
        handleExpandPC={handleExpandPC}
        handleEditClick={handleEditClick}
        logsVariant={logsVariant}
        expandedIndex={expandedIndex}
        setExpandedIndex={setExpandedIndex}
        variantDetailsSelected={variantDetailsSelected}
        expandVC={expandVC}
        handleExpandVC={handleExpandVC}
        handleVariantToggle={handleVariantToggle}
        handleAddVariant={handleAddVariant}
        loading={loading}
        handleExpandVCLogs={handleExpandVCLogs}
        expandVCLogs={expandVCLogs}
        vehicleStatusData={vehicleStatusData}
        logsCountVC={logsCountVC}
        rowsPerPageVCLogCount={rowsPerPageVCLogCount}
        pageVCLogCount={pageVCLogCount}
        handlePageChangeLogsVC={handlePageChangeLogsVC}
        isReceratedProject={isReceratedProject}
        handleRecreateVariantClick={handleRecreateVariantClick}
        handleBackToPC={handleBackToPC}
        tabsData1={tabsData1}
        tableProjectConfigs={tableProjectConfigs}
        projectId={projectDetailsSelected?.project_id || projectId}
      />
      {console.log(
        "selectedValuesUpdateProject[dialogTitleProject]",
        selectedValuesUpdateProject[dialogTitleProject],
      )}
      {openAddVariant && (
        <AddDialog
          disabled={isAddDisabledVC}
          componentMap={componentMap}
          radioOptions={radioOptions}
          formLayout={formLayout}
          singleColumnRows={singleColumnRows}
          open={openAddVariant}
          onClose={() => handleOpenCloseAddVariant(false)}
          title={"Create Variant"}
          handleClickAddDialog={handleClickAddDialogVariant}
        />
      )}
      {openRecreationVariant && (
        <AddDialog
          disabled={isAddDisabledVC}
          componentMap={componentMap}
          radioOptions={radioOptions}
          formLayout={formLayout}
          singleColumnRows={[1, 2, 3, 5, 6, 7, 8, 9, 10]}
          open={openRecreationVariant}
          onClose={() => {
            handleCloseRecreateVariant(false);
            handleOpenCloseAddVariant(false);
          }}
          title={"Re-create Variant"}
          handleClickAddDialog={handleClickRecreateDialogVariant}
        />
      )}
      {openUpdateMilestoneDialog && (
        <AddDialog
          disabled={isUpdateMilestoneDisabled}
          componentMap={componentMap}
          radioOptions={radioOptions}
          formLayout={updateMilestoneFormLayout}
          singleColumnRows={[1]}
          open={openUpdateMilestoneDialog}
          onClose={handleCloseUpdateMilestoneDialog}
          title={"Update Milestone"}
          titleAlign="left"
          hideDividers
          handleClickAddDialog={handleUpdateMilestones}
        />
      )}
      {openDetailsDialog && (
        <CustomDialogBoxV2
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          title={`Add ${formatDialogTitle(dialogTitleProject)}`}
          successButtonName="Add"
          rejectButtonName="Cancel"
          otherButtonName="Re-direct"
          actionButtonStyle={"continuous"}
          onClickSuccess={() =>
            handleClickEdit(dialogTitleProject?.toUpperCase())
          }
          onClickReject={() => setOpenDetailsDialog(false)}
          onOtherButtonClick={() => { }}
        >
          {(dialogTitleProject === "POWERTRAIN CLASS" ||
            dialogTitleProject === "VEHICLE FUNCTION(PROJECT)" ||
            dialogTitleProject === "VEHICLE FUNCTION(VARIANT)") && (
              <Box sx={{ width: "600px" }}>
                <CustomMultiSelectV2
                  selectedValues={selectedValuesUpdateProject[dialogTitleProject]}
                  setSelectedValues={handleSelectionChange}
                  optionsList={getOptionsList(
                    selectedValuesUpdateProject[dialogTitleProject],
                  )}
                />
              </Box>
            )}

          {dialogTitleProject === "MANUFACTURER CONFIGURATIONS" ||
            dialogTitleProject === "DELIVERY MARKET" ? (
            <Box sx={{ width: "500px", ml: -1 }}>
              <CustomSelectV3
                // enableSearch={true}
                placeholder={`Select/Search`}
                selectedValue={selectedValuesUpdateProject[dialogTitleProject]}
                handleOnChange={(e) =>
                  setSelectedValuesUpdateProject((prev) => ({
                    ...prev,
                    [dialogTitleProject]: e.target.value,
                  }))
                }
                optionsList={
                  dialogTitleProject === "DELIVERY MARKET"
                    ? adminDefaultData?.data?.product_delivery_markets
                      ?.map((item) => ({
                        label: item,
                        value: item,
                      }))
                      ?.filter((obj) =>
                        projectDetailsSelected?.delivery_market
                          ? !projectDetailsSelected.delivery_market.includes(
                            obj.value,
                          )
                          : true,
                      ) || []
                    : dialogTitleProject === "MANUFACTURER CONFIGURATIONS"
                      ? adminDefaultData?.data?.vehicle_manufacturers
                        ?.map((item) => ({
                          label: item?.name,

                          value: item,
                        }))
                        ?.filter((obj) =>
                          projectDetailsSelected?.mft_details
                            ? !projectDetailsSelected?.mft_details.includes({
                              label: obj?.name,
                              value: obj,
                            })
                            : true,
                        ) || []
                      : []
                }
              />
            </Box>
          ) : null}
        </CustomDialogBoxV2>
      )}
    </Box>
  );
}

export default ViewProjectConfig;
